// src/data/geographyContent2.js
// Grade 9 Ready — Geography Content Section 2
// Section Two: Tectonic Activity
//
// Source basis:
// - Grade 9 Ready geography source material: Section Two — Tectonic Activity
// - AQA GCSE Geography 8035: 3.1.1.2 Tectonic hazards

// src/data/geographyContent2.js
// Grade 9 Ready — Geography Content Section 2
// Section Two: Tectonic Activity

export const geographyContent2 = {
  id: "geography-section-2",
  sectionNumber: 2,
  slug: "tectonic-activity",
  title: "Section Two — Tectonic Activity",
    subtitle: "Geological time, plate tectonics, earthquakes, volcanoes and hazard management",
    examBoardLinks: ["AQA GCSE Geography 8035 — 3.1.1.2 Tectonic hazards"],
    overview:
      "This section introduces the geological timescale and explains how plate tectonics creates earthquakes, volcanoes and fold mountains. It builds towards GCSE hazard thinking by exploring why tectonic hazards affect places differently and how monitoring, prediction, protection and planning reduce risk.",
    bigIdeas: [
      "The Earth has changed over very long geological timescales.",
      "The crust is split into tectonic plates that move slowly over the mantle.",
      "Different plate margins create different hazards and landforms.",
      "Earthquakes and volcanoes are unevenly distributed because they mainly occur at plate boundaries.",
      "The impact of tectonic hazards depends on both physical processes and human vulnerability.",
      "We cannot stop tectonic hazards, but we can reduce risk through monitoring, prediction, protection and planning."
    ],
    keywords: [
      "geological timescale",
      "crust",
      "mantle",
      "core",
      "tectonic plate",
      "plate margin",
      "constructive boundary",
      "destructive boundary",
      "conservative boundary",
      "subduction",
      "earthquake",
      "focus",
      "epicentre",
      "seismic waves",
      "Richter scale",
      "volcano",
      "magma",
      "lava",
      "ash",
      "shield volcano",
      "composite volcano",
      "primary effects",
      "secondary effects",
      "immediate response",
      "long-term response",
      "monitoring",
      "prediction",
      "protection",
      "planning",
      "LEDC",
      "MEDC",
      "LIC",
      "HIC"
    ],
    topics: [
      {
        id: "geo-2-1-geological-timescale",
        slug: "geological-timescale",
        title: "The Geological Timescale",
        unit: "Tectonic Activity",
        learningObjectives: [
          "Understand that Earth’s history is divided into geological time periods.",
          "Explain why geological change usually happens over very long timescales.",
          "Recognise that some major Earth changes are sudden, but most are gradual."
        ],
        coreKnowledge: [
          {
            heading: "Earth is ancient",
            points: [
              "The Earth is around 4.6 billion years old.",
              "Geologists divide Earth’s history into large blocks of time called eras and periods.",
              "These divisions help scientists organise major changes in rocks, life, climate and landforms."
            ]
          },
          {
            heading: "Geological time is much longer than human time",
            points: [
              "Human history covers only a tiny part of Earth’s history.",
              "Processes such as mountain building, erosion and plate movement usually happen over millions of years.",
              "Some events, such as earthquakes and volcanic eruptions, happen suddenly but are caused by long-term tectonic processes."
            ]
          },
          {
            heading: "Why it matters for geography",
            points: [
              "Understanding geological time helps explain why landscapes, continents and oceans have changed.",
              "It also helps explain why earthquakes and volcanoes occur in particular zones today."
            ]
          }
        ],
        workedExamples: [
          {
            question: "Why is the geological timescale useful?",
            answer:
              "The geological timescale is useful because it organises Earth’s very long history into sections. This helps geographers and geologists understand when major changes happened, such as the formation of rocks, the movement of continents and the development of life."
          },
          {
            question: "Explain why plate movement is difficult to notice in everyday life.",
            answer:
              "Plate movement is difficult to notice because plates usually move only a few centimetres per year. Over a human lifetime this seems tiny, but over millions of years it can move continents, create mountain ranges and form oceans."
          }
        ],
        misconceptions: [
          "Geological time does not mean hundreds of years; it usually means millions or billions of years.",
          "Slow processes can still create huge changes when they happen over very long periods.",
          "Earthquakes are sudden events, but the pressure that causes them builds up over long periods."
        ],
        quickCheck: [
          {
            question: "What is the geological timescale?",
            options: [
              "A scale used to measure earthquake strength",
              "A way of dividing Earth’s history into time periods",
              "A map showing where volcanoes are found",
              "A method for predicting volcanic eruptions"
            ],
            answer: "A way of dividing Earth’s history into time periods",
            explanation:
              "The geological timescale divides Earth’s long history into named periods and eras."
          },
          {
            question: "Why do geographers study geological time?",
            options: [
              "To understand long-term Earth processes",
              "To predict tomorrow’s weather",
              "To calculate population density",
              "To measure river discharge"
            ],
            answer: "To understand long-term Earth processes",
            explanation:
              "Processes such as plate movement, mountain building and rock formation happen over very long timescales."
          }
        ],
        grade9Move:
          "Link timescale to process: explain that sudden hazards such as earthquakes are the visible result of pressure that has built up over long geological periods."
      },
      {
        id: "geo-2-2-earths-structure-and-plate-tectonics",
        slug: "earths-structure-and-plate-tectonics",
        title: "The Earth’s Structure and Plate Tectonics",
        unit: "Tectonic Activity",
        learningObjectives: [
          "Describe the basic structure of the Earth.",
          "Explain what tectonic plates are.",
          "Understand why plates move."
        ],
        coreKnowledge: [
          {
            heading: "The structure of the Earth",
            points: [
              "The crust is the thin, solid outer layer of the Earth.",
              "The mantle lies below the crust and contains semi-molten rock that can move slowly.",
              "The core is extremely hot and is found at the centre of the Earth."
            ]
          },
          {
            heading: "Tectonic plates",
            points: [
              "The crust is broken into large pieces called tectonic plates.",
              "These plates fit together like a jigsaw.",
              "Plates move very slowly, usually only a few centimetres each year."
            ]
          },
          {
            heading: "Why plates move",
            points: [
              "Heat from inside the Earth creates movement in the mantle.",
              "This movement helps drag or push plates across the Earth’s surface.",
              "Where plates meet, pressure builds and creates tectonic hazards."
            ]
          }
        ],
        workedExamples: [
          {
            question: "Explain why the crust is described as being like a jigsaw.",
            answer:
              "The crust is described as being like a jigsaw because it is broken into separate pieces called tectonic plates. These plates fit together and cover the Earth’s surface, but they are not fixed in place."
          },
          {
            question: "Why do plate boundaries often have earthquakes and volcanoes?",
            answer:
              "Plate boundaries often have earthquakes and volcanoes because this is where plates interact. They may move apart, collide, or slide past each other. These movements build pressure, fracture rock, melt rock, or allow magma to rise."
          }
        ],
        misconceptions: [
          "The crust is not one single shell; it is divided into plates.",
          "The mantle is not a liquid ocean, but it can flow very slowly.",
          "Plate movement is slow, but the hazards it causes can be sudden."
        ],
        quickCheck: [
          {
            question: "What are tectonic plates?",
            options: [
              "Large pieces of the Earth’s crust",
              "Cloud belts around the Earth",
              "Layers of soil",
              "Large ocean waves"
            ],
            answer: "Large pieces of the Earth’s crust",
            explanation:
              "Tectonic plates are large sections of the crust and upper mantle that move slowly."
          },
          {
            question: "What causes plates to move?",
            options: [
              "Population growth",
              "Ocean tides only",
              "Heat and movement within the mantle",
              "Wind and rainfall"
            ],
            answer: "Heat and movement within the mantle",
            explanation:
              "Heat inside the Earth helps create movement in the mantle, which contributes to plate movement."
          }
        ],
        grade9Move:
          "Use cause-and-effect sequencing: internal heat → mantle movement → plate movement → pressure at margins → tectonic hazards."
      },
      {
        id: "geo-2-3-plate-boundaries",
        slug: "plate-boundaries",
        title: "Plate Boundaries",
        unit: "Tectonic Activity",
        learningObjectives: [
          "Identify constructive, destructive and conservative plate boundaries.",
          "Explain the processes at each type of plate margin.",
          "Link plate margin type to earthquakes, volcanoes and fold mountains."
        ],
        coreKnowledge: [
          {
            heading: "Constructive plate boundaries",
            points: [
              "Constructive boundaries occur where plates move apart.",
              "Magma rises to fill the gap and cools to form new crust.",
              "These boundaries can create volcanoes and gentle earthquakes."
            ]
          },
          {
            heading: "Destructive plate boundaries",
            points: [
              "Destructive boundaries occur where plates move towards each other.",
              "The denser oceanic plate is forced beneath the continental plate in a process called subduction.",
              "Subduction can create powerful earthquakes, explosive volcanoes and fold mountains."
            ]
          },
          {
            heading: "Conservative plate boundaries",
            points: [
              "Conservative boundaries occur where plates slide past each other.",
              "The plates may lock due to friction, causing pressure to build.",
              "When the pressure is released, an earthquake occurs.",
              "Volcanoes do not usually form at conservative boundaries because crust is not created or destroyed."
            ]
          }
        ],
        workedExamples: [
          {
            question: "Explain how a destructive plate boundary can create a volcano.",
            answer:
              "At a destructive plate boundary, an oceanic plate is forced beneath a continental plate because it is denser. As the oceanic plate sinks into the mantle, it melts. This creates magma, which rises through cracks in the crust. If the magma reaches the surface, it erupts as a volcano."
          },
          {
            question: "Why are earthquakes common at conservative boundaries?",
            answer:
              "Earthquakes are common at conservative boundaries because two plates slide past each other. Friction can cause the plates to lock. Pressure builds until it is suddenly released, causing the ground to shake."
          }
        ],
        misconceptions: [
          "Not all plate boundaries produce volcanoes.",
          "Conservative boundaries produce earthquakes but usually not volcanoes.",
          "Constructive boundaries create new crust; destructive boundaries destroy crust.",
          "The word destructive does not mean every event is a disaster; it refers to crust being destroyed."
        ],
        quickCheck: [
          {
            question: "At which plate boundary do plates move apart?",
            options: [
              "Constructive",
              "Destructive",
              "Conservative",
              "Collision-only"
            ],
            answer: "Constructive",
            explanation:
              "At constructive boundaries, plates move apart and new crust is formed."
          },
          {
            question: "Why do volcanoes usually not form at conservative boundaries?",
            options: [
              "The plates are too cold",
              "There is no magma anywhere on Earth",
              "Crust is not being created or destroyed",
              "Earthquakes stop magma from rising"
            ],
            answer: "Crust is not being created or destroyed",
            explanation:
              "At conservative boundaries, plates slide past each other, so magma is not usually created."
          },
          {
            question: "What is subduction?",
            options: [
              "When two plates move apart",
              "When one plate is forced beneath another",
              "When magma cools above ground",
              "When ash settles after an eruption"
            ],
            answer: "When one plate is forced beneath another",
            explanation:
              "Subduction occurs when a denser plate, usually oceanic, is forced beneath another plate."
          }
        ],
        grade9Move:
          "Always name the plate margin, describe plate movement, then explain the resulting process and hazard."
      },
      {
        id: "geo-2-4-earthquakes",
        slug: "earthquakes",
        title: "Earthquakes",
        unit: "Tectonic Activity",
        learningObjectives: [
          "Explain how earthquakes happen.",
          "Use key earthquake terminology accurately.",
          "Understand how earthquake strength is measured."
        ],
        coreKnowledge: [
          {
            heading: "How earthquakes happen",
            points: [
              "Earthquakes happen when pressure builds up in the crust and is suddenly released.",
              "This often happens at plate boundaries where plates are locked by friction.",
              "The released energy travels as seismic waves, causing the ground to shake."
            ]
          },
          {
            heading: "Focus and epicentre",
            points: [
              "The focus is the point inside the Earth where the earthquake starts.",
              "The epicentre is the point on the Earth’s surface directly above the focus.",
              "Shaking is often strongest near the epicentre."
            ]
          },
          {
            heading: "Measuring earthquakes",
            points: [
              "Earthquake magnitude is commonly measured using the Richter scale.",
              "A higher magnitude means a much stronger earthquake.",
              "The damage caused also depends on depth, distance from the epicentre, building quality, population density and preparedness."
            ]
          }
        ],
        workedExamples: [
          {
            question: "Explain the difference between the focus and the epicentre.",
            answer:
              "The focus is the point inside the Earth where the earthquake begins. The epicentre is the point on the surface directly above the focus. The epicentre is often where the strongest shaking is felt."
          },
          {
            question: "Why might two earthquakes of the same magnitude cause different levels of damage?",
            answer:
              "Two earthquakes of the same magnitude can cause different damage because vulnerability varies. A shallow earthquake near a densely populated city with weak buildings may cause more damage than a deeper earthquake in a rural area with stronger buildings and better emergency planning."
          }
        ],
        misconceptions: [
          "Magnitude is not the only factor affecting damage.",
          "The epicentre is on the surface, not underground.",
          "Earthquakes can occur without volcanoes, especially at conservative boundaries."
        ],
        quickCheck: [
          {
            question: "What is the focus of an earthquake?",
            options: [
              "The point on the surface above the earthquake",
              "The underground point where the earthquake starts",
              "The largest wave in a tsunami",
              "The building most damaged by shaking"
            ],
            answer: "The underground point where the earthquake starts",
            explanation:
              "The focus is the point inside the Earth where the earthquake begins."
          },
          {
            question: "What is the epicentre?",
            options: [
              "The centre of the Earth",
              "The point on the surface directly above the focus",
              "The edge of a volcano",
              "The scale used to measure earthquakes"
            ],
            answer: "The point on the surface directly above the focus",
            explanation:
              "The epicentre is the surface point directly above the focus."
          }
        ],
        grade9Move:
          "For evaluation questions, separate the physical hazard from human vulnerability. High magnitude does not automatically mean highest impact."
      },
      {
        id: "geo-2-5-volcanoes",
        slug: "volcanoes",
        title: "Volcanoes",
        unit: "Tectonic Activity",
        learningObjectives: [
          "Explain how volcanoes form.",
          "Compare composite, shield and dome volcanoes.",
          "Understand why some volcanoes are more explosive than others."
        ],
        coreKnowledge: [
          {
            heading: "How volcanoes form",
            points: [
              "Volcanoes form when magma rises through cracks in the crust.",
              "When magma reaches the surface, it is called lava.",
              "Eruptions can release lava, ash, gases and volcanic bombs."
            ]
          },
          {
            heading: "Composite volcanoes",
            points: [
              "Composite volcanoes are usually steep-sided.",
              "They are built from alternating layers of lava and ash.",
              "They often have thick, sticky lava and can erupt explosively.",
              "They are commonly found at destructive plate boundaries."
            ]
          },
          {
            heading: "Shield volcanoes",
            points: [
              "Shield volcanoes are wide with gentle slopes.",
              "They usually produce runny lava that travels long distances.",
              "Their eruptions are often less explosive than composite volcanoes.",
              "They are commonly found at constructive plate boundaries and hotspots."
            ]
          },
          {
            heading: "Dome volcanoes",
            points: [
              "Dome volcanoes form from very thick lava.",
              "The lava does not travel far, so it builds up near the vent.",
              "Pressure can build, making eruptions dangerous."
            ]
          }
        ],
        workedExamples: [
          {
            question: "Compare composite and shield volcanoes.",
            answer:
              "Composite volcanoes are steep-sided and made from layers of ash and lava. They often have thick lava and explosive eruptions. Shield volcanoes are wider with gentle slopes and are formed by runny lava that travels further, producing less explosive eruptions."
          },
          {
            question: "Why are composite volcanoes often more dangerous?",
            answer:
              "Composite volcanoes are often more dangerous because their lava is thick and sticky. This can block vents and allow pressure to build. When the pressure is released, eruptions can be explosive and produce ash, pyroclastic flows and volcanic bombs."
          }
        ],
        misconceptions: [
          "Not all volcanoes are cone-shaped.",
          "Lava and magma are not exactly the same: magma is underground; lava is at the surface.",
          "Runny lava usually creates wider, flatter volcanoes.",
          "Explosive eruptions are often linked to thick, sticky lava and trapped gas."
        ],
        quickCheck: [
          {
            question: "What is magma called when it reaches the surface?",
            options: ["Ash", "Lava", "Core", "Crust"],
            answer: "Lava",
            explanation:
              "Magma is molten rock below the surface. Once it reaches the surface, it is called lava."
          },
          {
            question: "Which volcano type is usually broad with gentle slopes?",
            options: [
              "Composite volcano",
              "Shield volcano",
              "Dome volcano",
              "Fold volcano"
            ],
            answer: "Shield volcano",
            explanation:
              "Shield volcanoes are broad and gently sloping because runny lava spreads out over a large area."
          },
          {
            question: "Which volcano type is commonly made from alternating layers of lava and ash?",
            options: [
              "Composite volcano",
              "Shield volcano",
              "Conservative volcano",
              "Ocean trench"
            ],
            answer: "Composite volcano",
            explanation:
              "Composite volcanoes are built from layers of lava and ash."
          }
        ],
        grade9Move:
          "Explain eruption style using lava viscosity: thick, sticky lava traps gas and increases explosiveness."
      },
      {
        id: "geo-2-6-global-distribution-of-tectonic-hazards",
        slug: "global-distribution-of-tectonic-hazards",
        title: "Global Distribution of Tectonic Hazards",
        unit: "Tectonic Activity",
        learningObjectives: [
          "Describe the global distribution of earthquakes and volcanoes.",
          "Explain why tectonic hazards are concentrated along plate margins.",
          "Recognise major tectonic zones such as the Pacific Ring of Fire."
        ],
        coreKnowledge: [
          {
            heading: "Hazards are not randomly distributed",
            points: [
              "Most earthquakes and volcanoes occur in long, narrow belts.",
              "These belts usually follow plate boundaries.",
              "This is because plate movement creates pressure, friction, melting and fractures."
            ]
          },
          {
            heading: "The Pacific Ring of Fire",
            points: [
              "The Pacific Ring of Fire is a major zone of earthquakes and volcanoes around the Pacific Ocean.",
              "It is linked to several plate boundaries, especially destructive margins.",
              "Many powerful earthquakes and explosive volcanoes occur in this zone."
            ]
          },
          {
            heading: "Exceptions and variation",
            points: [
              "Some volcanoes occur away from plate boundaries at hotspots.",
              "Earthquake risk varies depending on plate margin type, depth and local geology.",
              "Hazard risk also depends on human factors such as population density and preparedness."
            ]
          }
        ],
        workedExamples: [
          {
            question: "Describe the global distribution of earthquakes and volcanoes.",
            answer:
              "Earthquakes and volcanoes are mainly found in narrow belts along plate boundaries. A major example is the Pacific Ring of Fire, where many volcanoes and earthquakes occur around the edge of the Pacific Ocean. They are less common in the middle of plates, although hotspot volcanoes can occur away from plate boundaries."
          },
          {
            question: "Explain why tectonic hazards are common at plate boundaries.",
            answer:
              "Tectonic hazards are common at plate boundaries because this is where plates move apart, collide or slide past each other. These movements can cause pressure to build and be released as earthquakes, or allow magma to rise and erupt as volcanoes."
          }
        ],
        misconceptions: [
          "Tectonic hazards are not spread evenly around the world.",
          "Volcanoes are not found only in hot countries.",
          "A country can have high tectonic risk because of its plate boundary location, not because of its climate."
        ],
        quickCheck: [
          {
            question: "Where do most earthquakes and volcanoes occur?",
            options: [
              "Along plate boundaries",
              "Only in deserts",
              "Only near the equator",
              "In the centre of every continent"
            ],
            answer: "Along plate boundaries",
            explanation:
              "Most earthquakes and volcanoes happen where tectonic plates meet."
          },
          {
            question: "What is the Pacific Ring of Fire?",
            options: [
              "A tropical rainforest region",
              "A major zone of earthquakes and volcanoes around the Pacific Ocean",
              "A desert belt in Africa",
              "A warm ocean current"
            ],
            answer: "A major zone of earthquakes and volcanoes around the Pacific Ocean",
            explanation:
              "The Pacific Ring of Fire is a major tectonic zone around the Pacific Ocean."
          }
        ],
        grade9Move:
          "Use distribution language: concentrated, clustered, linear belts, plate margins, uneven distribution."
      },
      {
        id: "geo-2-7-surviving-tectonic-hazards",
        slug: "surviving-tectonic-hazards",
        title: "Surviving Tectonic Hazards",
        unit: "Tectonic Activity",
        learningObjectives: [
          "Explain why people live in tectonic hazard zones.",
          "Describe how scientists monitor tectonic hazards.",
          "Explain how planning, prediction, protection and preparation reduce risk."
        ],
        coreKnowledge: [
          {
            heading: "Why people live in hazard zones",
            points: [
              "Volcanic soils can be fertile for farming.",
              "Tourism can create jobs near volcanoes and dramatic landscapes.",
              "Geothermal energy can provide heat and electricity.",
              "People may have family, cultural or economic ties to the area.",
              "Some people cannot afford to move away."
            ]
          },
          {
            heading: "Monitoring and prediction",
            points: [
              "Scientists can monitor volcanoes using gas measurements, ground deformation, temperature changes and small earthquakes.",
              "Earthquakes are much harder to predict accurately.",
              "Monitoring can give warning signs, but it cannot always predict the exact time, location and magnitude of a hazard."
            ]
          },
          {
            heading: "Protection",
            points: [
              "Buildings can be designed to withstand shaking.",
              "Flexible foundations, cross-bracing and automatic shut-off systems can reduce earthquake damage.",
              "Lava barriers and diversion channels may reduce volcanic risk in some places."
            ]
          },
          {
            heading: "Planning and preparation",
            points: [
              "Evacuation plans help people leave danger zones quickly.",
              "Hazard maps identify high-risk areas.",
              "Emergency drills, warning systems and public education improve survival chances.",
              "Emergency supplies, rescue teams and communication systems help reduce deaths after an event."
            ]
          }
        ],
        workedExamples: [
          {
            question: "Why do people continue to live near volcanoes?",
            answer:
              "People continue to live near volcanoes because the benefits can be significant. Volcanic soils are fertile, tourism can create income, and geothermal energy can be used for power. Some people also remain because their family, work or culture is tied to the area, or because they cannot afford to move."
          },
          {
            question: "Explain how planning can reduce earthquake risk.",
            answer:
              "Planning can reduce earthquake risk by preparing people before the event. Governments can create evacuation plans, run drills, educate communities, map danger zones and prepare emergency services. This does not stop the earthquake, but it can reduce deaths and speed up response."
          },
          {
            question: "Why is earthquake prediction difficult?",
            answer:
              "Earthquake prediction is difficult because scientists cannot yet accurately identify the exact time, location and magnitude of most earthquakes. They can monitor risk zones and detect patterns, but precise prediction remains unreliable."
          }
        ],
        misconceptions: [
          "Prediction and monitoring are not the same thing.",
          "Hazard management does not stop tectonic hazards; it reduces their impacts.",
          "Earthquakes are harder to predict than volcanic eruptions.",
          "People do not live in hazard zones simply because they do not understand the risk."
        ],
        quickCheck: [
          {
            question: "Which option is an example of protection against earthquakes?",
            options: [
              "Earthquake-resistant building design",
              "Moving tectonic plates apart",
              "Stopping seismic waves",
              "Removing the mantle"
            ],
            answer: "Earthquake-resistant building design",
            explanation:
              "Protection reduces damage, for example by designing buildings to withstand shaking."
          },
          {
            question: "Which option is an example of planning?",
            options: [
              "Evacuation drills",
              "Subduction",
              "Lava viscosity",
              "The Richter scale"
            ],
            answer: "Evacuation drills",
            explanation:
              "Planning prepares people and services before a hazard happens."
          },
          {
            question: "Why might people live near volcanoes?",
            options: [
              "Fertile soils and tourism opportunities",
              "There is never any risk",
              "Volcanoes only erupt once",
              "Earthquakes cannot happen near volcanoes"
            ],
            answer: "Fertile soils and tourism opportunities",
            explanation:
              "Volcanic areas can provide benefits, including fertile soil, tourism and geothermal energy."
          }
        ],
        grade9Move:
          "In hazard-management answers, distinguish between reducing probability, reducing vulnerability and reducing impact. Tectonic hazards cannot usually be prevented, so the best answers focus on reducing vulnerability and improving response."
      },
      {
        id: "geo-2-8-tectonic-hazards-contrasting-wealth",
        slug: "tectonic-hazards-contrasting-wealth",
        title: "Tectonic Hazards in Countries of Contrasting Wealth",
        unit: "Tectonic Activity",
        learningObjectives: [
          "Explain why tectonic hazards affect richer and poorer countries differently.",
          "Distinguish between primary and secondary effects.",
          "Distinguish between immediate and long-term responses.",
          "Evaluate why wealth influences hazard preparation and recovery."
        ],
        coreKnowledge: [
          {
            heading: "Primary and secondary effects",
            points: [
              "Primary effects happen directly because of the hazard, such as buildings collapsing, roads cracking or people being injured.",
              "Secondary effects happen as a result of the primary effects, such as fires, disease, homelessness, landslides or economic disruption.",
              "Secondary effects can sometimes cause more long-term damage than the original event."
            ]
          },
          {
            heading: "Immediate and long-term responses",
            points: [
              "Immediate responses happen during or soon after the event, such as search and rescue, emergency shelter, medical aid and food distribution.",
              "Long-term responses happen later and include rebuilding, restoring infrastructure, improving building codes and supporting economic recovery."
            ]
          },
          {
            heading: "Why wealth matters",
            points: [
              "Richer countries often have stronger buildings, better emergency services and more money for recovery.",
              "Poorer countries may have weaker infrastructure, less access to medical care and fewer resources for rebuilding.",
              "However, wealth is not the only factor. Governance, population density, corruption, education, time of day and physical geography also affect impacts."
            ]
          },
          {
            heading: "LEDCs and MEDCs",
            points: [
              "Older resources may use the terms LEDC and MEDC.",
              "At GCSE, you may also see LIC, NEE and HIC.",
              "The key point is to compare places with contrasting levels of wealth and development."
            ]
          }
        ],
        workedExamples: [
          {
            question: "Explain why an earthquake may cause more deaths in a poorer country than in a richer country.",
            answer:
              "An earthquake may cause more deaths in a poorer country because buildings may be less able to withstand shaking, emergency services may be slower or less well equipped, and hospitals may have fewer resources. Poorer communities may also live in more vulnerable housing or on unstable land."
          },
          {
            question: "What is the difference between a primary and secondary effect?",
            answer:
              "A primary effect is caused directly by the hazard, such as buildings collapsing during an earthquake. A secondary effect happens because of the primary effect, such as fires breaking out after gas pipes are damaged."
          },
          {
            question: "Why should GCSE answers avoid saying richer countries are always safe?",
            answer:
              "Richer countries are not always safe because very powerful hazards can still cause major damage. Also, local factors such as population density, building age, coastal location and preparedness affect risk. Wealth usually improves protection and response, but it does not remove hazard risk."
          }
        ],
        misconceptions: [
          "Richer countries can still suffer serious damage.",
          "Poorer countries are not more tectonically active because they are poor; poverty affects vulnerability and response.",
          "Primary effects are direct; secondary effects are indirect.",
          "Immediate responses are short-term actions; long-term responses are about recovery and rebuilding."
        ],
        quickCheck: [
          {
            question: "Which is a primary effect of an earthquake?",
            options: [
              "Buildings collapse during shaking",
              "Tourism falls for several years",
              "Disease spreads weeks later",
              "Businesses lose income during reconstruction"
            ],
            answer: "Buildings collapse during shaking",
            explanation:
              "Primary effects are the direct effects of the hazard."
          },
          {
            question: "Which is a secondary effect of a volcanic eruption?",
            options: [
              "Lava leaves the vent",
              "Ash is erupted into the atmosphere",
              "Flights are cancelled because of ash clouds",
              "Magma rises through the crust"
            ],
            answer: "Flights are cancelled because of ash clouds",
            explanation:
              "Cancelled flights are an indirect consequence of the ash cloud, so this is a secondary effect."
          },
          {
            question: "Which is a long-term response?",
            options: [
              "Search and rescue",
              "Emergency first aid",
              "Rebuilding homes to higher standards",
              "Handing out bottled water in the first 24 hours"
            ],
            answer: "Rebuilding homes to higher standards",
            explanation:
              "Rebuilding is part of long-term recovery."
          }
        ],
        grade9Move:
          "Avoid simplistic rich-versus-poor answers. Strong answers weigh wealth alongside governance, preparedness, building quality, population density, physical geography and hazard magnitude."
      }
    ],
    sectionPractice: {
      warmUpQuestions: [
        {
          question: "Name the three main layers of the Earth.",
          suggestedAnswer: "Crust, mantle and core."
        },
        {
          question: "What is a tectonic plate?",
          suggestedAnswer: "A large section of the Earth’s crust and upper mantle that moves slowly."
        },
        {
          question: "Give one example of a constructive plate boundary process.",
          suggestedAnswer: "Plates move apart and magma rises to create new crust."
        },
        {
          question: "What is the difference between magma and lava?",
          suggestedAnswer: "Magma is molten rock below the surface; lava is molten rock at the surface."
        },
        {
          question: "Why are earthquakes common at conservative boundaries?",
          suggestedAnswer:
            "Plates slide past each other, become locked by friction, and then suddenly release pressure."
        }
      ],
      examStyleQuestions: [
        {
          marks: 2,
          command: "Define",
          question: "Define the term tectonic hazard.",
          indicativeContent:
            "A natural hazard caused by movement of tectonic plates, such as an earthquake or volcanic eruption."
        },
        {
          marks: 3,
          command: "Describe",
          question: "Describe the global distribution of earthquakes and volcanoes.",
          indicativeContent:
            "They are unevenly distributed; most are found in narrow belts along plate boundaries; a major concentration is around the Pacific Ring of Fire."
        },
        {
          marks: 4,
          command: "Explain",
          question: "Explain how earthquakes occur at conservative plate boundaries.",
          indicativeContent:
            "Plates slide past each other; friction causes them to lock; pressure builds; when pressure is released suddenly, seismic waves are produced and the ground shakes."
        },
        {
          marks: 6,
          command: "Explain",
          question:
            "Explain how monitoring, prediction, protection and planning can reduce the effects of tectonic hazards.",
          indicativeContent:
            "Monitoring identifies warning signs; prediction may allow evacuation, especially for volcanoes; protection such as earthquake-resistant buildings reduces damage; planning such as drills, hazard maps and emergency services reduces deaths and improves response."
        },
        {
          marks: 9,
          command: "Evaluate",
          question:
            "‘The level of wealth of a country is the most important factor affecting the impact of tectonic hazards.’ To what extent do you agree?",
          indicativeContent:
            "Agree: richer countries often have stronger buildings, better emergency services, monitoring, planning and faster recovery. Disagree: magnitude, depth, time of day, population density, governance, physical geography and preparedness also matter. Best answers make a balanced judgement."
        }
      ],
      objectiveQuestions: [
        {
          question: "Which plate boundary creates new crust?",
          options: ["Constructive", "Destructive", "Conservative", "Collision"],
          answer: "Constructive"
        },
        {
          question: "Which plate boundary is most associated with subduction?",
          options: ["Constructive", "Destructive", "Conservative", "Transform-only"],
          answer: "Destructive"
        },
        {
          question: "At which boundary do plates slide past each other?",
          options: ["Constructive", "Destructive", "Conservative", "Divergent"],
          answer: "Conservative"
        },
        {
          question: "What is the point inside the Earth where an earthquake starts called?",
          options: ["Epicentre", "Focus", "Vent", "Crater"],
          answer: "Focus"
        },
        {
          question: "What is the point on the surface directly above the focus called?",
          options: ["Epicentre", "Focus", "Mantle", "Subduction zone"],
          answer: "Epicentre"
        },
        {
          question: "Which volcano type is usually steep and explosive?",
          options: ["Shield", "Composite", "River", "Conservative"],
          answer: "Composite"
        },
        {
          question: "Which volcano type usually has runny lava and gentle slopes?",
          options: ["Shield", "Composite", "Dome", "Fold"],
          answer: "Shield"
        },
        {
          question: "Which is an example of a secondary effect?",
          options: [
            "Ground shaking",
            "A building collapses immediately",
            "Disease spreads after water supplies are damaged",
            "A lava flow leaves the vent"
          ],
          answer: "Disease spreads after water supplies are damaged"
        },
        {
          question: "Which is an immediate response?",
          options: [
            "Rebuilding a city over ten years",
            "Changing national building codes",
            "Search and rescue after an earthquake",
            "Long-term economic regeneration"
          ],
          answer: "Search and rescue after an earthquake"
        },
        {
          question: "Which strategy is an example of planning?",
          options: [
            "Evacuation drills",
            "Seismic waves",
            "Subduction",
            "Lava viscosity"
          ],
          answer: "Evacuation drills"
        }
      ],
      aiChallenge: {
        title: "Grade 9 Tectonic Hazard Judgement",
        prompt:
          "A major earthquake affects two cities. City A is in a richer country with strict building codes, trained emergency services and regular drills. City B is in a poorer country with weaker buildings, limited medical care and high population density. Explain why the same magnitude earthquake could have different impacts in each city. Then evaluate whether wealth is the most important factor.",
        successCriteria: [
          "Explain physical hazard factors such as magnitude, depth and distance from epicentre.",
          "Explain human vulnerability factors such as building quality, population density, preparedness and emergency response.",
          "Use the terms primary effect, secondary effect, immediate response and long-term response accurately.",
          "Make a balanced judgement rather than simply saying rich countries are safe and poor countries are unsafe."
        ]
      }
    },
  revisionSummary: [
    "The Earth’s crust is divided into tectonic plates.",
    "Most earthquakes and volcanoes occur at plate boundaries.",
    "Constructive boundaries move apart and create new crust.",
    "Destructive boundaries involve subduction and can create explosive volcanoes, earthquakes and fold mountains.",
    "Conservative boundaries involve plates sliding past each other and commonly produce earthquakes.",
    "The focus is where an earthquake starts; the epicentre is the point on the surface directly above it.",
    "Composite volcanoes are steep and often explosive; shield volcanoes are broad with gentler slopes.",
    "Primary effects happen directly because of the hazard; secondary effects happen as a result of primary effects.",
    "Immediate responses happen during or soon after the event; long-term responses focus on recovery and rebuilding.",
    "Monitoring, prediction, protection and planning can reduce tectonic hazard risk, but they cannot stop plate movement."
  ]
};

export default geographyContent2;